/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';

import { Button, CheckboxGroup, ContextualHelp, Search, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import useDebounce from 'bundles/common/hooks/useDebounce';
import { useEnterpriseAdminSearchContext } from 'bundles/enterprise-admin-product-search/context';
import TrackedButton from 'bundles/page/components/TrackedButton';
import CourseraPlusSearchFilter from 'bundles/search-common/components/filters/CourseraPlusSearchFilter';
import SearchFilterItem from 'bundles/search-common/components/filters/SearchFilterItem';
import {
  ENTERPRISE_COLLECTION_ENTITIES,
  SEARCH_MAIN_INDEX_ID,
  THRESHOLD_FOR_FILTER_GROUP_BECOMING_SEARCHABLE,
} from 'bundles/search-common/constants';
import { IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchFilterEventingData, SearchFilterOption } from 'bundles/search-common/types';
import type { FacetConfig } from 'bundles/search-common/types/filter';
import { useSortedFilterItems } from 'bundles/search-common/utils/filterSortingUtils';
import useStableSearchResults from 'bundles/search-common/utils/useStableSearchResults';
import { removeWhiteSpace, scrollUpIfNecessary } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

export const FILTER_PREVIEW_COUNT = 4;
export const FILTER_BATCH_SIZE = 100;

const scrollOnApply = () => {
  scrollUpIfNecessary({ mustBeScrolledPastThisElementId: 'search-results-header-wrapper' });
};

export type UnappliedFiltersByFacet = {
  [facetName: string]: string[];
};

type Props = {
  groupConfig: FacetConfig;
  groupValues: SearchFilterOption[];
  eventingData: SearchFilterEventingData;
  isChip?: boolean;
  shouldHideGroupLabel?: boolean;
  onCollapse?: (collapseShowMore: () => void) => void;
  shouldAutoApply?: boolean;
  onFilterChange?: (unappliedFilters: UnappliedFiltersByFacet) => void;
  unappliedFilters?: string[];
  isAllFiltersPanel?: boolean;
};

const styles = {
  search: css`
    margin: 8px 0 12px;

    .cds-input-root {
      width: 100%;
    }
  `,
  checkboxGroup: css`
    width: 100%;
  `,
  showMoreButton: css`
    width: fit-content;
    text-decoration: underline;
    color: var(--cds-color-neutral-primary);
    padding: 0;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
  `,
};

function filterFilterOptions(
  searchInputValue: string,
  isEnterpriseAdminSearch: boolean
): (value: SearchFilterOption, index: number, array: SearchFilterOption[]) => boolean {
  return (item) => {
    const itemMatchesSearchQuery =
      !searchInputValue || item.label.toLowerCase().includes(searchInputValue.toLowerCase());
    const productTypeIncludedInEnterprise =
      !isEnterpriseAdminSearch || (isEnterpriseAdminSearch && !ENTERPRISE_COLLECTION_ENTITIES.includes(item.label));
    return itemMatchesSearchQuery && productTypeIncludedInEnterprise;
  };
}

const getCustomFilterGroupLabel = (): Record<string, string> => ({
  productTypeDescription: _t('Product type'),
  productDifficultyLevel: _t('Difficulty level'),
});

// Disabling because the below as it's cleaner not to refactor into a component and renderLabe is expecting (labelElement: React.ReactElement) => React.ReactElement;
// eslint-disable-next-line react/display-name
const getFilterGroupTooltipRenderer = (group: FacetConfig) => (labelComponent: React.ReactElement) =>
  (
    <ContextualHelp
      helpToggleProps={{
        'aria-label': _t('Information about this filter group'),
      }}
      label={labelComponent}
    >
      {/* Should the experimental variants be successful, move this to FACETS_CONFIG */}
      {group.property === 'productTypeDescription' ? (
        <FormattedHTMLMessage
          message={_t(`
          <p><strong>Courses:</strong> Learn from top instructors with graded assignments, videos, and discussion forums.</p>
          <p><strong>Professional certificate:</strong> Earn career credentials from industry leaders that demonstrate your expertise.</p>
          <p><strong>Specializations:</strong> Get in-depth knowledge of a subject by completing a series of courses and projects.</p>
          <p><strong>Degrees:</strong> Earn your Bachelor’s or Master’s degree online for a fraction of the cost of in-person learning.</p>
          <p><strong>Projects:</strong> Learn job-relevant skills in under 2 hours with interactive, hands-on tutorials.</p>
          <p><strong>Postgraduate & Graduate Diplomas:</strong> Take graduate-level courses without getting a full degree, or earn a university-issued career credential online.</p>
        `)}
        />
      ) : (
        <Typography2 component="p">{group?.getToolTip?.()}</Typography2>
      )}
    </ContextualHelp>
  );

export const CheckableExpandableSearchFilterGroup = (props: Props) => {
  const {
    groupConfig,
    groupValues = [],
    eventingData,
    isChip,
    shouldHideGroupLabel,
    onCollapse,
    shouldAutoApply,
    onFilterChange,
    unappliedFilters,
    isAllFiltersPanel,
  } = props;

  const [visibleItemsCount, setVisibleItemsCount] = useState(
    groupConfig.showAllItems ? Number.MAX_SAFE_INTEGER : FILTER_PREVIEW_COUNT
  );
  const [searchInputValue, setSearchInputValue] = useState('');
  const isEnterpriseAdminSearch = useEnterpriseAdminSearchContext();
  const groupShouldExpandCollapse = !groupConfig.showAllItems;
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const track = useRetracked();
  const trackV3 = useTracker();

  const { addFilters, removeFilters, isLoading } = useSearch();
  const stableSearchResults = useStableSearchResults(SEARCH_MAIN_INDEX_ID);
  const filterGroupName = groupConfig.getName();
  const filterGroupKey = groupConfig.property;

  // Memoize the current filters to prevent excessive re-renders
  const currentFilters = useMemo(() => {
    if (shouldAutoApply) {
      const facetFilters = [
        ...(stableSearchResults?.facetFilters || []),
        ...(stableSearchResults?.autoAppliedFilters || []),
      ];
      return facetFilters.flat();
    }
    return unappliedFilters || [];
  }, [shouldAutoApply, stableSearchResults?.facetFilters, stableSearchResults?.autoAppliedFilters, unappliedFilters]);

  const isFilterApplied = useCallback<(filterValue: string) => boolean>(
    (filterValue: string) => {
      return currentFilters.includes(`${filterGroupKey}:${filterValue}`);
    },
    [currentFilters, filterGroupKey]
  );

  const filteredGroupValues = useMemo(
    () => groupValues?.filter(filterFilterOptions(debouncedSearchQuery, isEnterpriseAdminSearch)),
    [groupValues, debouncedSearchQuery, isEnterpriseAdminSearch]
  );
  const sortedFilterOptions = useSortedFilterItems(filteredGroupValues ?? [], filterGroupKey, {
    sortByCount: true,
    enrichWithSupportText: true,
  });

  const itemsToShow = sortedFilterOptions?.slice(0, visibleItemsCount) || [];
  const totalItems = sortedFilterOptions?.length || 0;
  const isShowingAll = visibleItemsCount >= totalItems;

  const debouncedRefine = useDebounce(() => {
    setDebouncedSearchQuery(searchInputValue);
    track({
      action: 'search',
      trackingName: 'filter_query_change',
      trackingData: {
        newSearchValue: removeWhiteSpace(searchInputValue),
        filterCategory: filterGroupKey,
        page: window?.location.pathname,
      },
    });
  }, 250);

  const onClear = useCallback<() => $TSFixMe>(() => {
    setDebouncedSearchQuery('');
    setVisibleItemsCount(FILTER_PREVIEW_COUNT);
  }, []);

  // Method to collapse the "Show more" state
  const collapseShowMore = useCallback<() => void>(() => {
    if (visibleItemsCount > FILTER_PREVIEW_COUNT && groupShouldExpandCollapse) {
      setVisibleItemsCount(FILTER_PREVIEW_COUNT);
    }
  }, [visibleItemsCount, groupShouldExpandCollapse]);

  const collapseShowMoreRef = useRef(collapseShowMore);
  collapseShowMoreRef.current = collapseShowMore;

  const onCollapseRef = useRef(onCollapse);
  onCollapseRef.current = onCollapse;

  useEffect(() => {
    if (onCollapseRef.current) {
      onCollapseRef.current(() => collapseShowMoreRef.current());
    }
  }, []);

  const addFiltersToMainIndex = useCallback<(filter: string) => void>(
    (filter: string) => {
      if (groupShouldExpandCollapse && visibleItemsCount > FILTER_PREVIEW_COUNT) {
        setVisibleItemsCount(FILTER_PREVIEW_COUNT);
      }
      addFilters(SEARCH_MAIN_INDEX_ID, [filter]);
      if (shouldAutoApply) {
        scrollOnApply();
      }
      trackV3('apply_filter', {
        filter: [filter],
        filterSource: 'unified_modal',
      });
    },
    [addFilters, trackV3, groupShouldExpandCollapse, visibleItemsCount, shouldAutoApply]
  );

  const removeFiltersFromMainIndex = useCallback<(filter: string) => void>(
    (filter: string) => {
      if (groupShouldExpandCollapse && visibleItemsCount > FILTER_PREVIEW_COUNT) {
        setVisibleItemsCount(FILTER_PREVIEW_COUNT);
      }
      removeFilters(SEARCH_MAIN_INDEX_ID, [filter]);
      if (shouldAutoApply) {
        scrollOnApply();
      }
      trackV3('apply_filter', {
        filter: [filter],
        filterSource: 'unified_modal',
      });
    },
    [removeFilters, trackV3, groupShouldExpandCollapse, visibleItemsCount, shouldAutoApply]
  );

  const handleFilterCheck = (value: string) => {
    if (shouldAutoApply) {
      addFiltersToMainIndex(`${filterGroupKey}:${value}`);
    } else if (onFilterChange && unappliedFilters) {
      onFilterChange({ [filterGroupKey]: [...unappliedFilters, value] });
    }
  };

  const handleFilterUncheck = (value: string) => {
    if (shouldAutoApply) {
      removeFiltersFromMainIndex(`${filterGroupKey}:${value}`);
    } else if (onFilterChange && unappliedFilters) {
      onFilterChange({ [filterGroupKey]: unappliedFilters.filter((filterValue) => filterValue !== value) });
    }
  };
  if (groupValues.length === 0) {
    return null;
  }

  if (filterGroupKey === IS_PART_OF_COURSERA_PLUS) {
    return (
      <CourseraPlusSearchFilter
        name={filterGroupName}
        items={groupValues}
        addFilters={shouldAutoApply ? addFiltersToMainIndex : handleFilterCheck}
        removeFilters={shouldAutoApply ? removeFiltersFromMainIndex : handleFilterUncheck}
        eventingData={eventingData}
        isRefined={isFilterApplied('true')}
      />
    );
  }

  return (
    <div data-testid={`search-filter-group-${filterGroupName}`}>
      <CheckboxGroup
        label={
          filterGroupKey === IS_PART_OF_COURSERA_PLUS || shouldHideGroupLabel
            ? undefined
            : getCustomFilterGroupLabel()[filterGroupKey] || groupConfig.getName()
        }
        renderLabel={
          (filterGroupKey === 'productTypeDescription' || groupConfig.getToolTip?.()) && !shouldHideGroupLabel
            ? getFilterGroupTooltipRenderer(groupConfig)
            : undefined
        }
        aria-label={_t('Filter by #{filterGroupName}', { filterGroupName })}
        css={styles.checkboxGroup}
      >
        <>
          {/* only show search when relevant */}
          {groupValues.length > THRESHOLD_FOR_FILTER_GROUP_BECOMING_SEARCHABLE && (
            <Search
              hideSearchButton
              placeholder={_t('Search #{filterGroupName} (#{count})', {
                filterGroupName,
                count: groupValues.length,
              })}
              value={searchInputValue}
              onChange={(value) => {
                if (!isLoading) {
                  debouncedRefine();
                  setSearchInputValue(value);
                }
              }}
              onClear={isLoading ? undefined : onClear}
              css={styles.search}
              disabled={isLoading}
            />
          )}

          {/* No results */}
          {debouncedSearchQuery && sortedFilterOptions.length === 0 && (
            <Typography2 component="p" variant="bodyPrimary">
              {_t('No results found')}
            </Typography2>
          )}
        </>
        <>
          {/* list of checkboxes */}
          {itemsToShow.map((filterItem) => {
            const { label, value, count } = filterItem;
            return (
              <SearchFilterItem
                {...filterItem}
                label={label}
                key={label}
                value={`${filterGroupKey}:${value}`}
                isRefined={isFilterApplied(value)}
                count={count}
                filterCategoryName={filterGroupName}
                addFilters={shouldAutoApply ? addFiltersToMainIndex : handleFilterCheck}
                removeFilters={shouldAutoApply ? removeFiltersFromMainIndex : handleFilterUncheck}
                eventingData={eventingData}
                shouldAlignResultsRight={isChip}
                showSupportText={true}
                isLoading={isLoading}
                isAllFiltersPanel={isAllFiltersPanel}
              />
            );
          })}

          {/* show "Show more/less" button for progressive loading */}
          {groupShouldExpandCollapse && totalItems > FILTER_PREVIEW_COUNT && (
            <Button
              data-testid="expand-filter-items-button"
              trackingName={`${isShowingAll ? 'collapse' : 'expand'}_filter_items_button_${filterGroupKey}`}
              withVisibilityTracking={false}
              requireFullyVisible={false}
              component={TrackedButton}
              variant="ghost"
              onClick={() => {
                if (isShowingAll) {
                  setVisibleItemsCount(FILTER_PREVIEW_COUNT);
                } else {
                  const nextCount = Math.min(visibleItemsCount + FILTER_BATCH_SIZE, totalItems);
                  setVisibleItemsCount(nextCount);
                }
              }}
              aria-label={_t('Show #{direction} #{filterGroupName} options', {
                filterGroupName,
                direction: isShowingAll ? 'less' : 'more',
              })}
              css={styles.showMoreButton}
            >
              {isShowingAll ? _t('Show less') : _t('Show more')}
            </Button>
          )}
        </>
      </CheckboxGroup>
    </div>
  );
};

export default CheckableExpandableSearchFilterGroup;
